import React, { useState } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import {
  WhatsAppIcon,
  TelegramIcon,
  WeChatIcon,
} from '../components/icons/SocialIcons';

// Single source of truth for every contact channel rendered on the
// page. Order here drives the card grid order — the most-direct
// channels (phone, WhatsApp) sit near the top so a visitor in a hurry
// taps them first.
const CONTACT = {
  address: { line1: 'Yerevan, Armenia', line2: 'str. Koxbaci, 53' },
  phone: '+374 55 433 001',
  whatsapp: '+374 55 433 001',
  email: 'contact@aranortravel.com',
  telegram: 'aranor_travel',
  wechat: 'aranor_travel',
};

// href helpers — normalize each value into the exact scheme the
// device expects. WhatsApp wants digits only; Telegram wants the
// handle without the leading @.
const telHref = (s: string) => `tel:${s.replace(/[^+\d]/g, '')}`;
const waHref = (s: string) => `https://wa.me/${s.replace(/[^\d]/g, '')}`;
const mailHref = (s: string) => `mailto:${s}`;
const tgHref = (s: string) => `https://t.me/${s.replace(/^@/, '')}`;

const VALUES = [
  {
    title: 'Local-first',
    body: 'Built in Yerevan, for everyone who walks Armenia. We pick venues ourselves, on foot.',
  },
  {
    title: 'No noise',
    body: 'You write a question, a human writes back. No ticket bot, no canned reply, no upsell.',
  },
  {
    title: 'EN / HY / RU',
    body: 'Three working languages, same team. Pick whichever feels closest to home.',
  },
  {
    title: '24h reply',
    body: 'Or your next stamp is on us. Weekends included — we travel too.',
  },
];

const Contact: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copyWeChat = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.wechat);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail (insecure context, permission denied)
      // — leave the UI silent rather than throwing; user can read
      // the ID off the card and copy by hand.
    }
  };

  return (
    <div className="space-y-12">
      {/* Hero — mirrors Privacy/Terms hero shape so the contact page
          reads as part of the same static-content family. */}
      <section className="-mx-4 px-6 lg:px-10 py-12 bg-brand-secondary text-white rounded-3xl space-y-5">
        <p className="font-heading font-bold text-xs tracking-[0.25em] text-brand-accent-light uppercase">
          Talk to us · 24h reply
        </p>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-warm-100/80">
          Let's get you{' '}
          <span className="italic text-brand-primary">stamped</span>.
        </h1>
        <p className="text-warm-300 max-w-2xl leading-relaxed">
          One human team behind every channel below. We answer in English,
          Armenian, and Russian — usually within a day, never longer than two.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Sidebar — "Our values" pillar, mirrors the Privacy
            "Need a human?" card shape. Sticky on desktop so it
            stays with the user as they scroll through the channels. */}
        <aside className="space-y-6 h-fit lg:sticky lg:top-24">
          <div className="space-y-2">
            <p className="font-heading font-bold text-[11px] tracking-[0.2em] text-warm-500 uppercase">
              Our values
            </p>
            <p className="text-warm-600 text-sm leading-relaxed">
              Four rules we keep, so you know what to expect before you write.
            </p>
          </div>
          <div className="bg-brand-secondary text-white rounded-2xl p-6 space-y-5">
            {VALUES.map((v) => (
              <div key={v.title} className="space-y-1">
                <p className="font-heading font-bold text-sm text-brand-primary">
                  {v.title}.
                </p>
                <p className="text-warm-300 text-sm leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </aside>

        {/* Body — every contact channel as a tap-to-action card. */}
        <div className="space-y-6">
          <div className="space-y-2">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
              How to reach us
            </p>
            <h2 className="font-display text-3xl text-warm-800">
              Pick any channel
            </h2>
            <p className="text-warm-600 leading-relaxed max-w-2xl">
              Tap a card on mobile to open the app directly — call, WhatsApp,
              Telegram, or mail. WeChat copies the ID to your clipboard.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Address — display only, no link. */}
            <ChannelCard
              icon={<MapPin className="w-5 h-5" />}
              label="Address"
              primary={CONTACT.address.line1}
              secondary={CONTACT.address.line2}
            />

            {/* Phone */}
            <ChannelCard
              icon={<Phone className="w-5 h-5" />}
              label="Phone"
              primary={CONTACT.phone}
              href={telHref(CONTACT.phone)}
            />

            {/* WhatsApp */}
            <ChannelCard
              icon={<WhatsAppIcon className="w-5 h-5" />}
              label="WhatsApp"
              primary={CONTACT.whatsapp}
              href={waHref(CONTACT.whatsapp)}
              external
            />

            {/* Email */}
            <ChannelCard
              icon={<Mail className="w-5 h-5" />}
              label="Email"
              primary={CONTACT.email}
              href={mailHref(CONTACT.email)}
            />

            {/* Telegram */}
            <ChannelCard
              icon={<TelegramIcon className="w-5 h-5" />}
              label="Telegram"
              primary={`@${CONTACT.telegram}`}
              href={tgHref(CONTACT.telegram)}
              external
            />

            {/* WeChat — no cross-platform URL scheme; copy to clipboard. */}
            <ChannelCard
              icon={<WeChatIcon className="w-5 h-5" />}
              label="WeChat"
              primary={CONTACT.wechat}
              secondary={copied ? 'Copied ✓' : 'Tap to copy ID'}
              onClick={copyWeChat}
            />
          </div>

          <p className="text-warm-500 text-sm leading-relaxed pt-2">
            Press / partnerships /{' '}
            <a
              href={mailHref('contact@aranortravel.com')}
              className="text-brand-accent hover:underline"
            >
              contact@aranortravel.com
            </a>
            . For account-specific help, sign in first so we can find your
            passport faster.
          </p>
        </div>
      </div>

      {/* Toast — 2-second fade-up confirming the WeChat ID copy. Lives
          outside the card so it's visible above the fold on mobile
          even if the WeChat card scrolled past. */}
      {copied && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 z-50 bg-brand-secondary text-brand-primary font-heading font-bold text-sm px-5 py-3 rounded-xl shadow-lg"
        >
          WeChat ID copied
        </div>
      )}
    </div>
  );
};

// Channel card — either an `<a>` (when `href` is set), a `<button>`
// (when `onClick` is set), or a plain `<div>` (display-only, used for
// the postal address). Keeps the visual shape identical across all
// three so the grid reads as a uniform menu.
interface ChannelCardProps {
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
  icon,
  label,
  primary,
  secondary,
  href,
  external,
  onClick,
}) => {
  const inner = (
    <>
      <div className="w-10 h-10 rounded-lg bg-brand-primary/15 text-brand-accent flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0 space-y-0.5 text-left">
        <p className="font-heading font-bold text-[11px] tracking-[0.15em] text-warm-500 uppercase">
          {label}
        </p>
        <p className="font-heading font-bold text-warm-800 truncate">
          {primary}
        </p>
        {secondary && (
          <p className="text-warm-500 text-xs leading-relaxed">{secondary}</p>
        )}
      </div>
    </>
  );

  const className =
    'group bg-white border border-warm-200/80 rounded-2xl p-4 flex items-center gap-4 transition-colors hover:border-brand-primary hover:bg-warm-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent';

  if (href) {
    return (
      <a
        href={href}
        {...(external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
        className={className}
      >
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {inner}
      </button>
    );
  }
  return <div className={className}>{inner}</div>;
};

export default Contact;
